import React, {useCallback, useContext, useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';


import {BasicPage} from 'src/components/basic-page';
import {DateInput, FormInput, HtmlArea, TimeInput} from 'src/components/input';
import {RequiredMark} from 'src/components/required-mark';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {Skeleton} from 'src/components/skeleton';
import {DISCARD_CHANGES_SUCCESS_MESSAGE, SAVE_CHANGES_SUCCESS_MESSAGE} from 'src/constants';
import {useConfirmationChunk} from 'src/contexts/confirmation-context';
import {MessageContext} from 'src/contexts/message-context';
import {ValidatedControl, ValidationContextProvider} from 'src/contexts/validation-context';
import {ACCESS_RIGHTS} from 'src/hooks/access-rights';
import {AlertType} from 'src/openapi/admin/models/AlertType';

import Button from '@pb/kit-component-button';
import {CheckBox} from '@pb/kit-component-checkbox';
import ComboBox, {ComboboxItem} from '@pb/kit-component-combo-box';
import {Dialog, DialogFooter, DialogHeader, DialogMain} from '@pb/kit-component-dialog-v2';
import {Group, Item} from '@pb/kit-component-group';
import Label from '@pb/kit-component-label';
import {useShown} from '@pb/kit-hooks-use-shown';

import {ChangeLog} from '../change-log/change-log';
import {AccountSearch} from '../components/account-search';
import {
    ALERT_ACCOUNT_CONTROL,
    ALERT_END_DATE_CONTROL,
    ALERT_END_TIME_CONTROL,
    ALERT_MESSAGE_CONTROL,
    ALERT_START_DATE_CONTROL,
    ALERT_TYPE_CAPTIONS,
    MAX_HTML_LENGTH,
} from '../constants';

import {EditAlertSkeleton} from './edit-alert.skeleton';
import {useEditAlert} from './useEditAlert';

import type {AccessRight} from 'src/hooks/access-rights/constants';
import type {AccountShortInfo} from 'src/openapi/admin/models/AccountShortInfo';
import type {CheckBoxChangeData} from 'src/typedef';

const EditAlert = () => {
    const queryParams = useParams<{alertId: string}>();
    const alertId = queryParams.alertId ? +queryParams.alertId : -1;

    const navigate = useNavigate();
    const [busy, setBusy] = useState(false);
    const [reason, setReason] = useState('');
    const {shown: saveModalShown, onClick: openSaveModal, onClose: closeSaveModal} = useShown();
    const [messageRenderKey, setMessageRenderKey] = useState(0);
    const {setErrorMessage, setSuccessMessage} = useContext(MessageContext);

    const {
        isLoading: isAlertLoading,
        loadingError: alertLoadingError,
        data: request,
        setData: setRequest,
        isChanged: isRequestChanged,
        saveChanges: saveAlert,
        discardChanges: discardAlert,
    } = useEditAlert({alertId});

    const {
        proceed,
        setIsChunkChanged,
        isCurrentChunkChanged,
    } = useConfirmationChunk({chunkName: 'create-platform-alert', onProceed: () => {
        setSuccessMessage(SAVE_CHANGES_SUCCESS_MESSAGE);
        navigate('/platform-alert-builder');
    }});

    useEffect(() => {
        setIsChunkChanged(isRequestChanged);
    }, [isRequestChanged, setIsChunkChanged]);

    const backToActiveAlerts = useCallback(() => {
        navigate('/platform-alert-builder');
    }, [navigate]);

    const changeMessage = useCallback((message: string) => {
        setRequest(prev => ({...prev, message}));
    }, [setRequest]);

    const changeAccount = useCallback((account: AccountShortInfo | null) => {
        setRequest(prev => ({...prev, account}));
    }, [setRequest]);

    const changeAlertForAllUsers = useCallback((changeData: CheckBoxChangeData) => {
        const checked = changeData.event.currentTarget.checked;
        setRequest(prev => ({
            ...prev,
            forAllUsers: checked,
            account: checked ? null : prev.account,
        }));
    }, [setRequest]);

    const changeAlertType = useCallback((alertType: AlertType) => {
        setRequest(prev => ({...prev, alertType}));
    }, [setRequest]);

    const changeStartDate = useCallback((startDate: string) => {
        setRequest(prev => ({...prev, startDate}));
    }, [setRequest]);

    const changeStartTime = useCallback((startTime: string) => {
        setRequest(prev => ({...prev, startTime}));
    }, [setRequest]);

    const changeEndDate = useCallback((endDate: string) => {
        setRequest(prev => ({...prev, endDate}));
    }, [setRequest]);

    const changeEndTime = useCallback((endTime: string) => {
        setRequest(prev => ({...prev, endTime}));
    }, [setRequest]);

    const saveChanges = useCallback(async () => {
        setBusy(true);
        const error = await saveAlert(reason);
        setBusy(false);

        if (!error) {
            proceed();
        } else {
            closeSaveModal();
            setErrorMessage(error);
        }
    }, [saveAlert, proceed, setErrorMessage, reason, closeSaveModal]);

    const discardChanges = useCallback(() => {
        discardAlert();

        setMessageRenderKey(prev => prev + 1);
        setSuccessMessage(DISCARD_CHANGES_SUCCESS_MESSAGE);
    }, [discardAlert, setSuccessMessage]);

    return (
        <Skeleton shown={isAlertLoading} view={<EditAlertSkeleton/>}>
            <ServerResponseHandler
                serverError={alertLoadingError}
                accessRight={ACCESS_RIGHTS.PLATFORM_ALERT_BUILDER as AccessRight}
                sectionName="Edit Platform Alert">
                <BasicPage
                    title="Edit Alert"
                    buttonsBlock={(
                        <Group>
                            <Button variant="Type.TERTIARY" onClick={backToActiveAlerts}>
                                Cancel
                            </Button>
                            <Button
                                variant="Type.SECONDARY"
                                onClick={discardChanges}
                                disabled={!isCurrentChunkChanged || busy}>
                                Discard Changes
                            </Button>
                            <Button
                                variant="Type.PRIMARY"
                                onClick={openSaveModal}
                                disabled={!isCurrentChunkChanged}>
                                Save Changes
                            </Button>
                        </Group>
                    )}>
                    <Group>
                        <Item className="group-item pd-l">
                            <Group direction="v">
                                <div style={{width: 624}}>
                                    <Label><RequiredMark label="Message"/></Label>
                                    <ValidatedControl name={ALERT_MESSAGE_CONTROL}>
                                        {() => (
                                            <HtmlArea
                                                maxCharCount={MAX_HTML_LENGTH}
                                                key={messageRenderKey}
                                                value={request.message}
                                                onChange={changeMessage}
                                            />
                                        )}
                                    </ValidatedControl>
                                </div>
                                <CheckBox
                                    checked={request.forAllUsers}
                                    label="For All Users"
                                    onCheckBoxChange={changeAlertForAllUsers}
                                />
                                <div style={{width: 300}}>
                                    <Label>Account</Label>
                                    <ValidatedControl name={ALERT_ACCOUNT_CONTROL}>
                                        {() => (
                                            <AccountSearch
                                                disabled={request.forAllUsers}
                                                selectedAccount={request.account}
                                                onChange={changeAccount}
                                            />
                                        )}
                                    </ValidatedControl>
                                </div>
                                <div style={{width: 300}}>
                                    <Label>Type</Label>
                                    <ComboBox value={request.alertType} onChange={changeAlertType}>
                                        {Object.values(AlertType).map(type => (
                                            <ComboboxItem key={type} value={type} caption={ALERT_TYPE_CAPTIONS[type]}/>
                                        ))}
                                    </ComboBox>
                                </div>
                                <Group gap="l" alignItems="start">
                                    <div style={{width: 300}}>
                                        <ValidatedControl name={ALERT_START_DATE_CONTROL}>
                                            {() => (
                                                <DateInput
                                                    label={<RequiredMark label="Start Date"/>}
                                                    id="edit-alert-start-date"
                                                    selectedDate={request.startDate}
                                                    onChange={changeStartDate}
                                                />
                                            )}
                                        </ValidatedControl>
                                    </div>
                                    <div style={{width: 300}}>
                                        <ValidatedControl name={ALERT_END_DATE_CONTROL}>
                                            {() => (
                                                <DateInput
                                                    label={<RequiredMark label="End Date"/>}
                                                    id="edit-alert-end-date"
                                                    selectedDate={request.endDate}
                                                    onChange={changeEndDate}
                                                />
                                            )}
                                        </ValidatedControl>
                                    </div>
                                </Group>
                                <Group gap="l" alignItems="start">
                                    <div style={{width: 300}}>
                                        <Label>Start Time</Label>
                                        <TimeInput
                                            time={request.startTime}
                                            onBlur={changeStartTime}
                                        />
                                    </div>
                                    <div style={{width: 300}}>
                                        <ValidatedControl name={ALERT_END_TIME_CONTROL}>
                                            {() => (
                                                <div>
                                                    <Label>End Time</Label>
                                                    <TimeInput
                                                        time={request.endTime}
                                                        onBlur={changeEndTime}
                                                    />
                                                </div>
                                            )}
                                        </ValidatedControl>
                                    </div>
                                </Group>
                            </Group>
                        </Item>
                        <Item className="group-item pd-l">
                            <ChangeLog alertId={alertId}/>
                        </Item>
                    </Group>
                </BasicPage>
            </ServerResponseHandler>
            <Dialog
                shown={saveModalShown}
                setShown={closeSaveModal}
                wrapperProps={{style: {width: 400}}}>
                <DialogHeader onClose={closeSaveModal}>
                    Save Changes
                </DialogHeader>
                <DialogMain style={{padding: 24}}>
                    <Group direction="v">
                        Please provide a reason for updating the alert.
                        <FormInput
                            label="Reason"
                            value={reason}
                            maxLength={128}
                            placeholder="Enter reason"
                            onChange={setReason}
                        />
                    </Group>
                </DialogMain>
                <DialogFooter>
                    <Group>
                        <Button variant="Type.PRIMARY" onClick={saveChanges} disabled={!reason} busy={busy}>
                            Save
                        </Button>
                        <Button variant="Type.SECONDARY" onClick={closeSaveModal}>
                            Cancel
                        </Button>
                    </Group>
                </DialogFooter>
            </Dialog>
        </Skeleton>
    );
};

export const ValidatedEditAlert = () => (
    <ValidationContextProvider>
        <EditAlert/>
    </ValidationContextProvider>
);
